import { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { Send, CheckCircle2, XCircle, HelpCircle, RotateCw, Landmark, ThumbsUp } from "lucide-react"
import { getRoleCode } from "constant/Role"
import { isFinanceRole } from "shared/procurement/eligibleRoles"
import { Card, Button, Textarea, EmptyState } from "../../ui"
import { DeadlineField, useDeadlineFields, buildDeadlineParam } from "../DeadlineField"
import { formatVND, formatDate } from "../../constants"
import { collectActionNotes } from "../ActionNotes"

// Right-column action surface for the Payment Request Detail screen (F5).
// Buttons render ENTIRELY from `allowedActions` returned by the backend —
// never inferred from status client-side. Approve / Reject / Info Request go
// through the same Approval Rule Engine service used by OR/PO; this card only
// dispatches the picked action + note, the container owns the mutation.
export const PAYMENT_REQUEST_ACTION_META = {
  submit: {
    label: "Submit for Approval",
    variant: "primary",
    icon: Send,
    title: "Submit this payment request",
    confirm: "Submit",
    noteRequired: false,
    placeholder: "Add a note (optional)",
  },
  approve: {
    label: "Approve",
    variant: "primary",
    icon: CheckCircle2,
    title: "Approve this payment request",
    confirm: "Approve",
    noteRequired: false,
    placeholder: "Add a note (optional)",
  },
  reject: {
    label: "Reject",
    variant: "danger-outline",
    icon: XCircle,
    title: "Reject this payment request",
    confirm: "Reject",
    noteRequired: true,
    placeholder: "Reason for rejection",
  },
  info_request: {
    label: "Request Info",
    variant: "secondary",
    icon: HelpCircle,
    title: "Request more information",
    confirm: "Send request",
    noteRequired: true,
    placeholder: "What do you need before approving?",
  },
  resubmit: {
    label: "Resubmit",
    variant: "primary",
    icon: RotateCw,
    title: "Resubmit this payment request",
    confirm: "Resubmit",
    noteRequired: true,
    placeholder: "Describe what changed / the info provided",
  },
  send_to_accounting: {
    label: "Send to Accounting",
    variant: "primary",
    icon: Landmark,
    title: "Send this payment request to Accounting",
    confirm: "Send",
    noteRequired: false,
    placeholder: "Add a note (optional)",
  },
}

export const PAYMENT_REQUEST_ACTION_ORDER = [
  "submit",
  "resubmit",
  "approve",
  "reject",
  "info_request",
  "send_to_accounting",
]

export const allowedPaymentRequestActions = (actions = []) =>
  PAYMENT_REQUEST_ACTION_ORDER.filter((a) => actions.includes(a))

// Approve / reject / info_request follow `allowedActions` alone — BE computes
// them from the current step's real approvers (department/center managers
// included). Handing off to Accounting stays a Finance-only step.
const FINANCE_ONLY_ACTIONS = new Set(["send_to_accounting"])

const PaymentRequestActionBar = ({ allowedActions = [], paymentRequest, busy = false, onAct }) => {
  const canDecide = useSelector((s) => isFinanceRole(getRoleCode(s.auth?.role)))
  const actions = allowedPaymentRequestActions(allowedActions).filter(
    (a) => canDecide || !FINANCE_ONLY_ACTIONS.has(a)
  )
  const [activeAction, setActiveAction] = useState(null)
  const notes = collectActionNotes(paymentRequest?.actionLog, paymentRequest?.approvalProgress)

  return (
    <Card title="Approval Decision" subtitle={actions.length > 0 ? "Choose an action below" : undefined}>
      {actions.length > 0 ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div className="pc-defgrid" style={{ gridTemplateColumns: "1fr", marginBottom: 4 }}>
            <div>
              <div className="pc-def-label">Amount</div>
              <div className="pc-total-lg">{formatVND(paymentRequest?.amount)}</div>
            </div>
          </div>
          {actions.map((key) => {
            const meta = PAYMENT_REQUEST_ACTION_META[key]
            if (!meta) return null
            const Icon = meta.icon
            return (
              <Button
                key={key}
                block
                variant={meta.variant}
                leftIcon={<Icon aria-hidden size={16} />}
                disabled={busy}
                onClick={() => setActiveAction(key)}
              >
                {meta.label}
              </Button>
            )
          })}
        </div>
      ) : (
        <EmptyState
          icon={<ThumbsUp aria-hidden />}
          title="Nothing to act on"
          text="There's nothing for you to act on right now."
        />
      )}

      {notes.length > 0 && (
        <div style={{ marginTop: 16, paddingTop: 12, borderTop: "1px solid var(--pc-border)" }}>
          <div className="pc-def-label">Notes</div>
          {notes.map((n, i) => (
            <div key={i} style={{ marginTop: 8, fontSize: 13, overflowWrap: "anywhere" }}>
              <div className="pc-muted" style={{ fontSize: 12 }}>
                {n.actor} · {n.action}
                {n.at ? ` · ${formatDate(n.at)}` : ""}
              </div>
              “{n.note}”
            </div>
          ))}
        </div>
      )}

      {activeAction && (
        <PaymentRequestConfirmModal
          actionKey={activeAction}
          paymentRequest={paymentRequest}
          busy={busy}
          onCancel={() => !busy && setActiveAction(null)}
          onSubmit={async (note, deadline) => {
            const done = await onAct(activeAction, note, deadline)
            if (done) setActiveAction(null)
          }}
        />
      )}
    </Card>
  )
}

export const PaymentRequestConfirmModal = ({ actionKey, paymentRequest, busy, onCancel, onSubmit }) => {
  const meta = PAYMENT_REQUEST_ACTION_META[actionKey]
  const [note, setNote] = useState("")
  const { date, setDate, time, setTime } = useDeadlineFields(actionKey)

  useEffect(() => setNote(""), [actionKey])

  if (!meta) return null
  const invalid = meta.noteRequired && !note.trim()
  const Icon = meta.icon

  return (
    <div
      className="pc-modal-overlay"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel()
      }}
    >
      <div className="pc-modal">
        <div className="pc-modal-head">
          <div className="pc-modal-title">{meta.title}</div>
        </div>
        <div className="pc-modal-body">
          {(actionKey === "approve" || actionKey === "send_to_accounting" || actionKey === "submit") && (
            <div className="pc-alert pc-alert--success" role="status" style={{ marginBottom: 14 }}>
              <span className="pc-alert-ico">
                <Icon aria-hidden />
              </span>
              <div className="pc-alert-body">
                <div className="pc-alert-text">
                  {meta.label} <strong>{formatVND(paymentRequest?.amount)}</strong>
                  {paymentRequest?.paymentCode ? ` for ${paymentRequest.paymentCode}` : ""}
                  {(paymentRequest?.linkedPurchases || []).length > 0
                    ? ` (${paymentRequest.linkedPurchases.map((p) => p.poCode).join(", ")})`
                    : ""}
                  .
                </div>
              </div>
            </div>
          )}
          <Textarea
            label="Note"
            required={meta.noteRequired}
            placeholder={meta.placeholder}
            rows={4}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={busy}
          />
          {actionKey === "info_request" && (
            <DeadlineField
              date={date}
              time={time}
              onChangeDate={setDate}
              onChangeTime={setTime}
              disabled={busy}
            />
          )}
        </div>
        <div className="pc-modal-foot">
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant={meta.variant}
            onClick={() => onSubmit(note.trim(), buildDeadlineParam(date, time))}
            disabled={busy || invalid}
          >
            {busy ? "Submitting…" : meta.confirm}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default PaymentRequestActionBar
